import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { AnalyticsEvent, track } from '@bonusly/analytics';
import { useCurrentUser, useIdentity } from '@bonusly/who-am-i';

import { QueryKey as RecognitionBonusQueryKey } from '../../../components/recognition/bonus/hooks/types';
import { useBizyChannel } from '../../../hooks/use-bizy-channel';
import { executePageAction, useActionSchemaRegistry } from '../../../lib/bizy-actions';
import { useBizyHttpClient } from '../../../lib/bizy/bizy-http-client';
import { useBizyChatState, useBizyTimeline, type BizyResponseReceivedMeta } from '../../../lib/bizy/connector';
import type { ApprovalDecision, BizyChatMessage, BizyUserMessageNode, CardNode, ChatMessage, FeedbackType } from '../../../lib/bizy/types';
import { QueryKey as RecognitionFeedQueryKey } from '../../../modules/bonus-feed/features/recognition/api/hooks/types';
import { QueryKey as GrowthQueryKey } from '../../../modules/growth/types/growth-query-key';
import { bizyUnreadNotificationsQueryKey } from '../../../queries/bizy-notifications';
import { bizyPeopleQueryKey } from '../../../queries/bizy-people';
import { invalidateCelebrationsAdminCaches } from '../../../queries/celebrations';
import type { BizyState } from '../../bizy';
import type { NavigateFn } from '../bizy-navigation-context';
import { markBizyActivity } from '../lib/bizy-activity-stamp';
import { newestMessageTimestamp } from '../lib/bizy-message-timestamps';
import { clearBizySession, loadBizySession, saveBizySession } from '../lib/session-storage';
import { useBizyReportContext } from './use-bizy-report-context';

export type { ApprovalDecision, CardNode, ChatMessage, FeedbackType };

// A conversation the user idled away from and then replaced via a blank-slate
// send (BIZY-683). Stays rendered above a time divider while the new backend
// conversation lives below it. In-memory only — the archived view doesn't
// survive reloads.
export interface BizyArchivedSegment {
	messages: ChatMessage[];
	boundaryAt: Date;
}

interface UseBizyGlobalChatOptions {
	/**
	 * Extra fields merged into every chat POST's `context`. A getter is
	 * resolved when the POST body is built (send time), letting callers clear
	 * volatile entry-point state synchronously before a send that must not
	 * carry it — a plain object is captured per render as before.
	 */
	extraContext?: Record<string, string> | (() => Record<string, string>);
	model?: string;
	/** Override React Router navigate for non-SPA contexts (e.g. MFE on Rails pages). */
	navigateFn?: NavigateFn;
}

const EMOTION_CURIOUS: BizyState = 'curious';

let messageKeyCounter = 0;
function nextMessageKey(): string {
	return `msg-${Date.now()}-${messageKeyCounter++}`;
}

// ── union ⇆ flat mapping ──────────────────────────────────────────────
// The shared connector works in the `BizyChatMessage` discriminated union
// (assistant nodes on `nodes`); the chat view + session storage still consume
// the legacy flat `ChatMessage` (`assistantMessage`). Map at the hook boundary
// so consumers are untouched.

function toFlat(message: BizyChatMessage): ChatMessage {
	if (message.role === 'user') {
		return {
			role: 'user',
			content: message.content,
			key: message.key,
			timestamp: message.timestamp,
			attachmentPreviewUrl: message.attachmentPreviewUrl,
			nodes: message.nodes,
		};
	}
	return {
		role: 'assistant',
		content: message.content,
		id: message.id,
		key: message.key,
		feedbackType: message.feedbackType,
		nodeChoices: message.nodeChoices,
		toolsUsed: message.toolsUsed,
		timestamp: message.timestamp,
		assistantMessage: message.nodes.length ? message.nodes : undefined,
	};
}

function toUnion(message: ChatMessage): BizyChatMessage {
	if (message.role === 'user') {
		return {
			role: 'user',
			content: message.content,
			key: message.key ?? nextMessageKey(),
			timestamp: message.timestamp,
			attachmentPreviewUrl: message.attachmentPreviewUrl,
			nodes: message.nodes,
		};
	}
	return {
		role: 'assistant',
		content: message.content,
		id: message.id,
		key: message.key ?? nextMessageKey(),
		feedbackType: message.feedbackType,
		nodeChoices: message.nodeChoices,
		toolsUsed: message.toolsUsed,
		timestamp: message.timestamp,
		nodes: message.assistantMessage ?? [],
	};
}

const PATH = () => window.location.pathname;

// Returns the subset of `incoming` turns that are genuinely newer than anything
// in `current`, anchored on the newest known assistant message ID. Preserves
// user-bubble pairing — only turns after the last known assistant bubble are returned.
function mergeNewTurns(current: BizyChatMessage[], incoming: BizyChatMessage[]): BizyChatMessage[] {
	const newestKnownId = [...current].reverse().find((m) => m.role === 'assistant')?.id;
	if (!newestKnownId) return incoming;
	const anchorIdx = incoming.findIndex((m) => m.role === 'assistant' && m.id === newestKnownId);
	if (anchorIdx === -1) return incoming;
	return incoming.slice(anchorIdx + 1);
}

// Carries forward per-card choices the user already made (keyed by message
// id) so a timeline refresh doesn't reset post-click visual state.
function carryForwardNodeChoices(messages: BizyChatMessage[], sourceOfChoices: BizyChatMessage[]): BizyChatMessage[] {
	const choicesByMessageId = new Map<string, Record<number, ApprovalDecision>>();
	for (const msg of sourceOfChoices) {
		if (msg.role === 'assistant' && msg.id && msg.nodeChoices && Object.keys(msg.nodeChoices).length > 0) {
			choicesByMessageId.set(msg.id, msg.nodeChoices);
		}
	}
	if (choicesByMessageId.size === 0) return messages;
	return messages.map((msg) =>
		msg.role === 'assistant' && msg.id && choicesByMessageId.has(msg.id) ? { ...msg, nodeChoices: choicesByMessageId.get(msg.id) } : msg
	);
}

/**
 * Bizy streaming chat for admin pages. Thin app adapter over the shared
 * `useBizyChatState` connector (which owns the streaming state machine,
 * timeout/disconnect, request-id isolation, and the chat POST/cancel). This
 * layer adds the Kaleidoscope-specific concerns: session persistence, the
 * continuous timeline pager, page actions, report context, analytics,
 * navigation, and the union⇆flat message mapping.
 */
export function useBizyGlobalChat(options: UseBizyGlobalChatOptions = {}) {
	const { extraContext, model, navigateFn } = options;
	const sessionData = loadBizySession();

	const currentUser = useCurrentUser();
	const bizyHttp = useBizyHttpClient();
	const queryClient = useQueryClient();
	const reportContext = useBizyReportContext((state) => state.context);
	const { refetchProfile } = useIdentity();
	const routerNavigate = useNavigate();
	const navigate = navigateFn ?? routerNavigate;
	const { getSchema } = useActionSchemaRegistry();

	const refreshRecognitionSurfaces = useCallback(() => {
		queryClient.invalidateQueries([RecognitionFeedQueryKey.BonusesInfinite]);
		queryClient.invalidateQueries([RecognitionFeedQueryKey.Bonus]);
		queryClient.invalidateQueries([RecognitionFeedQueryKey.UserProfile]);
		queryClient.invalidateQueries([RecognitionBonusQueryKey.ProfileBonuses]);
		void refetchProfile();
	}, [queryClient, refetchProfile]);

	// Refs the async/page-action callbacks read synchronously. Synced from the
	// connector's reactive state below.
	const conversationIdRef = useRef<string | undefined>(sessionData?.conversationId);
	const isLoadingRef = useRef(false);
	const unionMessagesRef = useRef<BizyChatMessage[]>([]);
	const flatMessagesRef = useRef<ChatMessage[]>([]);
	const bizyEmotionRef = useRef<string>(EMOTION_CURIOUS);

	// Page-action handler — navigation + cache invalidation. Injected into the
	// connector (it resets the stream timeout before invoking this).
	const handlePageAction = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any -- the page_action payload is loosely typed at the wire
		async (data: any) => {
			try {
				if (data.action === 'navigate_to_page') {
					if (data.page_path) {
						const path = String(data.page_path);
						if (path.startsWith('/') && !path.startsWith('//')) {
							saveBizySession({
								messages: flatMessagesRef.current,
								conversationId: conversationIdRef.current,
								bizyEmotion: bizyEmotionRef.current as BizyState,
							});
							navigate(path);
							requestAnimationFrame(() => {
								const el = document.querySelector('[data-bizy-chat-scroll]');
								if (el) el.scrollTop = el.scrollHeight;
							});
						}
					}
					return;
				}
				if (data.action === 'invalidate_celebration_queries') {
					invalidateCelebrationsAdminCaches(queryClient);
					return;
				}
				if (data.action === 'invalidate_recognition_queries') {
					refreshRecognitionSurfaces();
					return;
				}
				if (data.action === 'invalidate_meetings_queries') {
					queryClient.invalidateQueries({ queryKey: [GrowthQueryKey.MeetingsV2] });
					return;
				}
				const schema = getSchema(data.report_type);
				await executePageAction(data, schema, navigate);
			} catch (error) {
				console.error('[Bizy] Failed to execute action:', error);
			}
		},
		[getSchema, navigate, queryClient, refreshRecognitionSurfaces]
	);

	// Compose the POST `context`: app surface + page path + report context +
	// hook-level extraContext + per-message overrides (highest precedence).
	const resolveContext = useCallback(
		(perMessageExtraContext?: Record<string, string>): Record<string, string> => ({
			surface: 'web',
			page_path: PATH(),
			...reportContext,
			...((typeof extraContext === 'function' ? extraContext() : extraContext) || {}),
			...(perMessageExtraContext || {}),
		}),
		[reportContext, extraContext]
	);

	const onMessageSent = useCallback((meta: { messageLength: number; conversationLength: number; isFirstMessage: boolean }) => {
		track(AnalyticsEvent.Bizy.ChatMessageSent, { ...meta, path: PATH() });
	}, []);

	const onResponseReceived = useCallback((meta: BizyResponseReceivedMeta) => {
		track(AnalyticsEvent.Bizy.ResponseReceived, { ...meta, path: PATH() });
	}, []);

	const onGenerationCancelled = useCallback(() => {
		track(AnalyticsEvent.Bizy.GenerationCancelled, { path: PATH() });
	}, []);

	// Seed the connector from the persisted session once.
	const initialMessages = useMemo(() => (sessionData?.messages ?? []).map(toUnion), []); // eslint-disable-line react-hooks/exhaustive-deps -- seed once from session

	// Continuous-chat timeline pager.
	const timeline = useBizyTimeline({ http: bizyHttp });

	const chat = useBizyChatState({
		http: bizyHttp,
		model,
		conversationId: sessionData?.conversationId,
		initialMessages,
		resolveContext,
		onPageAction: handlePageAction,
		onMessageSent,
		onResponseReceived,
		onGenerationCancelled,
		continuousMode: true,
	});

	// Map connector messages (union) → flat for consumers, and keep refs fresh.
	const messages = useMemo(() => chat.messages.map(toFlat), [chat.messages]);
	conversationIdRef.current = chat.conversationId;
	isLoadingRef.current = chat.isLoading;
	unionMessagesRef.current = chat.messages;
	flatMessagesRef.current = messages;
	bizyEmotionRef.current = chat.bizyEmotion;

	// Subscribe to BizyChannel; the connector supplies all the handlers. We
	// layer on `onObservedMomentSurfaced` here (not in the generic chat-state
	// connector) so the always-on panel subscription refreshes the People
	// Browser sidebar live when the dreaming pipeline surfaces a new moment.
	const channelOptions = useMemo(
		() => ({
			...chat.channelOptions,
			onObservedMomentSurfaced: () => {
				queryClient.invalidateQueries(bizyPeopleQueryKey());
				queryClient.invalidateQueries(bizyUnreadNotificationsQueryKey());
			},
		}),
		[chat.channelOptions, queryClient]
	);
	useBizyChannel(currentUser?.id, channelOptions);

	// Conversations archived by blank-slate sends (BIZY-683). Owned here —
	// next to the adapter that owns conversation identity — so every path
	// that replaces or empties the timeline (new chat, pagehide reset) clears
	// the archive in the same breath and the view can't drift out of sync
	// with it.
	const [archivedSegments, setArchivedSegments] = useState<BizyArchivedSegment[]>([]);

	// Surfaces a proactive turn (or the newest page of the timeline) rather
	// than switching to a specific conversation — the panel renders one
	// rolling timeline, so there's no per-conversation swap. Merges rather
	// than replaces so any older turns the user scrolled to are preserved.
	const loadConversation = useCallback((id: string) => {
		setArchivedSegments([]);
		timeline
			.loadNewest()
			.then((newMessages) => {
				if (newMessages.length === 0) return;
				const newTurns = mergeNewTurns(unionMessagesRef.current, newMessages);
				if (newTurns.length === 0) return;
				// Surfacing a proactive turn counts as recent activity (BIZY-692):
				// without this the timeline reads as stale the instant the turn
				// lands, so the idle blank slate arms and the "Let's boogie"
				// standby renders on top of Bizy's greeting. Stamping here — only
				// when there's a genuinely new turn — keeps the proactive message
				// front and centre. The true empty state (no turns to merge)
				// returns above without stamping, so a brand-new user still sees
				// standby.
				markBizyActivity();
				const convId = conversationIdRef.current ?? id;
				chat.loadMessages([...unionMessagesRef.current, ...newTurns], { conversationId: convId, bizyEmotion: EMOTION_CURIOUS });
			})
			.catch(() => {});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// ── Continuous-chat init ─────────────────────────────────────────
	// On first mount with no session, fetch the active segment id and load
	// the newest timeline page.
	const continuousInitRef = useRef(false);
	const [didInitLoad, setDidInitLoad] = useState(false);
	// Whether the *next* message would start a fresh chat segment rather than
	// append to the current one. Sourced from the server's 8h-boundary check on
	// `/bizy/chat/current_segment`; the value is transient (not derivable from
	// `messages`, since hydrated assistant/proactive turns carry no timestamp),
	// so we capture it here at load time. Consumers use it to decide whether to
	// show the new-segment greeter callout (BIZY-638).
	const [isNewSegment, setIsNewSegment] = useState(false);
	useEffect(() => {
		if (continuousInitRef.current) return;
		// Cached session: seed is already in the connector; mount self-heal below
		// refreshes from the server. Do NOT set didInitLoad here — pending Dive In
		// sends wait on it, and marking ready before self-heal finishes lets a
		// late loadMessages wipe the optimistic turn (session-path race).
		if (sessionData?.conversationId && sessionData.messages?.length) {
			continuousInitRef.current = true;
			return;
		}
		continuousInitRef.current = true;

		timeline
			.fetchCurrentSegment()
			.then(({ conversationId, isNewSegment: newSegment }) => {
				setIsNewSegment(newSegment);
				return timeline.loadNewest().then((newMessages) => {
					// Don't clobber an in-flight Dive In / user send — loadMessages
					// aborts the stream and replaces the optimistic turn.
					if (isLoadingRef.current) {
						setDidInitLoad(true);
						return;
					}
					chat.loadMessages(newMessages, { conversationId, bizyEmotion: EMOTION_CURIOUS });
					setDidInitLoad(true);
				});
			})
			.catch(() => {
				// Silently fail — start with empty panel state.
				setDidInitLoad(true);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	// Tab focus: refetch the newest timeline page to catch up on other tabs'
	// messages.
	useEffect(() => {
		const handleVisibilityChange = () => {
			if (document.visibilityState !== 'visible' || isLoadingRef.current) return;
			if (!conversationIdRef.current) return;
			timeline
				.loadNewest()
				.then((newMessages) => {
					if (newMessages.length === 0) return;
					// Merge only genuinely new turns to preserve any older turns the
					// user had scrolled up to load.
					const newTurns = mergeNewTurns(unionMessagesRef.current, newMessages);
					if (newTurns.length === 0) return;
					const combined = [...unionMessagesRef.current, ...newTurns];
					chat.loadMessages(carryForwardNodeChoices(combined, unionMessagesRef.current), {
						conversationId: conversationIdRef.current,
						bizyEmotion: bizyEmotionRef.current as BizyState,
					});
				})
				.catch(() => {});
		};
		document.addEventListener('visibilitychange', handleVisibilityChange);
		return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
	}, [timeline, chat]);

	// Mount self-heal: refresh segment id + newest timeline page in parallel
	// so a stale cached conversationId (e.g. after a segment boundary) is
	// corrected. A successful refetch supersedes content corrupted by
	// out-of-order streaming. Owns didInitLoad for the session-seeded path so
	// pending Dive In sends stay deferred until this replace has settled.
	useEffect(() => {
		if (!sessionData?.conversationId || !sessionData?.messages?.length) return;
		continuousInitRef.current = true;
		Promise.all([timeline.fetchCurrentSegment(), timeline.loadNewest()])
			.then(([{ conversationId, isNewSegment: newSegment }, newMessages]) => {
				setIsNewSegment(newSegment);
				if (newMessages.length === 0) return;
				// Same guard as continuous init / visibility: a hard replace would
				// erase an optimistic Dive In turn mid-stream.
				if (isLoadingRef.current) return;
				chat.loadMessages(carryForwardNodeChoices(newMessages, unionMessagesRef.current), { conversationId });
			})
			.catch(() => {})
			.finally(() => {
				setDidInitLoad(true);
			});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount with initial session data
	}, []);

	// ── Session persistence ───────────────────────────────────────────
	useEffect(() => {
		saveBizySession({ messages, conversationId: chat.conversationId, bizyEmotion: chat.bizyEmotion as BizyState });
	}, [messages, chat.conversationId, chat.bizyEmotion]);

	// ── Load older turns (infinite-scroll upward) ────────────────────
	// `onBeforePrepend` is called synchronously just before the state update so
	// the scroll-anchor hook can capture scrollHeight before the DOM grows.
	const loadOlderTurns = useCallback(
		async (onBeforePrepend?: () => void) => {
			const older = await timeline.loadOlder();
			if (older.length > 0) {
				onBeforePrepend?.();
				chat.prependMessages(older);
			}
		},
		[timeline, chat]
	);

	// ── Public actions (preserve legacy signatures) ───────────────────
	const sendMessage = useCallback(
		(
			content: string,
			perMessageExtraContext?: Record<string, string>,
			displayOptions?: { attachmentPreviewUrl?: string; userNodes?: BizyUserMessageNode[] }
		) => {
			markBizyActivity();
			chat.sendMessage(content, {
				extraContext: perMessageExtraContext,
				attachmentPreviewUrl: displayOptions?.attachmentPreviewUrl,
				userNodes: displayOptions?.userNodes,
			});
		},
		[chat]
	);

	// Send a message that opens a NEW backend conversation (BIZY-683 blank
	// slate). The visible conversation is archived above a time divider, then
	// the connector's `resetChat` clears its `conversationIdRef` synchronously
	// so the send POSTs without a conversation_id and the server allocates a
	// fresh one. The archive+reset+send pairing lives here — next to the
	// adapter that owns conversation identity — so callers can't get the
	// ordering wrong.
	const sendMessageInNewConversation = useCallback(
		(
			content: string,
			perMessageExtraContext?: Record<string, string>,
			displayOptions?: { attachmentPreviewUrl?: string; userNodes?: BizyUserMessageNode[] }
		) => {
			markBizyActivity();
			const visibleMessages = flatMessagesRef.current;
			if (visibleMessages.length > 0) {
				setArchivedSegments((previousSegments) => [
					...previousSegments,
					{ messages: visibleMessages, boundaryAt: newestMessageTimestamp(visibleMessages) ?? new Date() },
				]);
			}
			chat.resetChat();
			clearBizySession();
			chat.sendMessage(content, {
				extraContext: perMessageExtraContext,
				attachmentPreviewUrl: displayOptions?.attachmentPreviewUrl,
				userNodes: displayOptions?.userNodes,
			});
		},
		[chat]
	);

	const retryLastMessage = useCallback(() => {
		markBizyActivity();
		chat.retryLastMessage();
	}, [chat]);

	// A reply finishing (or being cancelled) is the end of an exchange — the
	// idle window is measured from it, not from the user's send.
	const wasLoadingRef = useRef(false);
	useEffect(() => {
		if (wasLoadingRef.current && !chat.isLoading && flatMessagesRef.current.length > 0) markBizyActivity();
		wasLoadingRef.current = chat.isLoading;
	}, [chat.isLoading]);

	const cancelGeneration = useCallback((): string | undefined => chat.cancelGeneration(), [chat]);

	const resetChat = useCallback(() => {
		setArchivedSegments([]);
		chat.resetChat();
		clearBizySession();
	}, [chat]);

	useEffect(() => {
		window.addEventListener('pagehide', resetChat);
		return () => window.removeEventListener('pagehide', resetChat);
	}, [resetChat]);

	return {
		messages,
		conversationId: chat.conversationId,
		isLoading: chat.isLoading,
		isTimedOut: chat.isTimedOut,
		isOtherTabLoading: chat.isOtherTabLoading,
		// No per-conversation swap to wait for — the panel renders one rolling
		// timeline, so a "load" is just a timeline refresh (see loadConversation).
		isLoadingConversation: false,
		bizyEmotion: chat.bizyEmotion as BizyState,
		activeToolCalls: chat.activeToolCalls,
		sendMessage,
		sendMessageInNewConversation,
		cancelGeneration,
		resetChat,
		retryLastMessage,
		updateMessageFeedback: chat.updateMessageFeedback,
		updateNodeChoice: chat.updateNodeChoice,
		loadConversation,
		archivedSegments,
		isContinuous: true,
		didInitLoad,
		isNewSegment,
		hasMoreHistory: timeline.hasMore,
		isLoadingOlder: timeline.isLoadingOlder,
		loadOlderTurns,
	};
}
